import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ADMIN_API_BASE_URL, currentAppMode } from "../config";

interface LoginProps {
  onLoginSuccess: (userData: any, token: string) => void;
  onForgotPassword: () => void;
}

export default function Login({
  onLoginSuccess,
  onForgotPassword,
}: LoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isSubmitting = useRef(false);

  // Handles the login submission
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (loading || isSubmitting.current) {
      console.log("Login already in progress, ignoring submission");
      return;
    }

    isSubmitting.current = true;
    setError(""); // Clear previous errors
    setLoading(true);

    // Basic frontend validation
    if (!email || !password) {
      setError("Please enter both email and password.");
      setLoading(false);
      isSubmitting.current = false;
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      isSubmitting.current = false;
      return;
    }

    // Password validation
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      setLoading(false);
      isSubmitting.current = false;
      return;
    }

    const requestData = {
      email: email.toLowerCase().trim(),
      password: password,
      appType: currentAppMode,
    };

    console.log("Attempting login with:", requestData);

    // Real API call to FastAPI backend
    try {
      const response = await fetch(`${ADMIN_API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      console.log("Response status:", response.status);
      console.log(
        "Response headers:",
        Object.fromEntries(response.headers.entries())
      );

      const data = await response.json();
      console.log("Response data:", data);

      if (response.ok) {
        // On successful login, save the token and admin details
        console.log("Login successful:", data);
        console.log(
          "🔍 Login: Storing token:",
          data.access_token.substring(0, 50) + "..."
        );
        console.log("🔍 Login: Storing user:", data.user);

        // Store token and user data securely
        localStorage.setItem("adminToken", data.access_token);
        localStorage.setItem("adminUser", JSON.stringify(data.user));
        localStorage.setItem(
          "tokenExpiry",
          new Date(Date.now() + data.expires_in * 1000).toISOString()
        );

        // Verify storage
        console.log(
          "🔍 Login: Token stored:",
          !!localStorage.getItem("adminToken")
        );
        console.log(
          "🔍 Login: User stored:",
          !!localStorage.getItem("adminUser")
        );

        // Call the success callback
        onLoginSuccess(data.user, data.access_token);
      } else {
        // Handle different error types
        console.error(
          "Login failed with status:",
          response.status,
          "and data:",
          data
        );
        if (response.status === 429) {
          setError(
            "Too many login attempts. Please wait 15 minutes before trying again."
          );
        } else if (response.status === 401) {
          setError("Invalid email or password. Please try again.");
        } else if (response.status === 422) {
          setError(
            `Validation error: ${data.detail || "Invalid request format"}`
          );
        } else {
          setError(data.detail || "Login failed. Please try again.");
        }
      }
    } catch (err) {
      console.error("Network error during login:", err);
      setError("Network error. Please check if the backend server is running.");
    } finally {
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 font-inter p-4 sm:p-6 lg:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Login Card */}
      <motion.div
        className="bg-white p-8 sm:p-10 rounded-2xl shadow-2xl w-full max-w-md border border-gray-100"
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        whileHover={{
          scale: 1.02,
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
          transition: { duration: 0.3 },
        }}
      >
        {/* TPL Logo */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <motion.div
            className="h-20 w-20 bg-gradient-to-br from-brand-blue to-brand-blue-dark text-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.5,
              type: "spring",
              stiffness: 200,
            }}
            whileHover={{
              rotate: 360,
              scale: 1.1,
              transition: { duration: 0.6 },
            }}
          >
            <span className="text-2xl font-bold tracking-wider">TPL</span>
          </motion.div>
          <motion.h1
            className="text-3xl font-bold text-brand-blue mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Admin Panel Login
          </motion.h1>
          <motion.p
            className="text-gray-600 text-sm"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            Access your platform management dashboard
          </motion.p>
        </motion.div>

        <motion.form
          onSubmit={handleLogin}
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          {/* Email Input */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email Address
            </label>
            <motion.input
              type="email"
              id="email"
              className="input-field"
              placeholder="admin@tpl.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
              whileFocus={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>

          {/* Password Input */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
          >
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Password
            </label>
            <div className="relative">
              <motion.input
                type={showPassword ? "text" : "password"}
                id="password"
                className="input-field pr-12"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
                minLength={8}
                whileFocus={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
              <motion.button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label={showPassword ? "Hide password" : "Show password"}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <AnimatePresence mode="wait">
                  {showPassword ? (
                    <motion.svg
                      key="hide"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      initial={{ opacity: 0, rotate: -90 }}
                      animate={{ opacity: 1, rotate: 0 }}
                      exit={{ opacity: 0, rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7A9.97 9.97 0 011.5 10.5m11.5 3.5l1.5 1.5M17.5 10.5l-1.5-1.5m-3.5 3.5l-3.5-3.5M4.125 4.125L2.5 2.5m15 15l-1.5-1.5M4.125 18.875L2.5 20.5m15-15l-1.5 1.5M10.5 1.5v-1.5m-1.5 3.5l-1.5-1.5"
                      />
                    </motion.svg>
                  ) : (
                    <motion.svg
                      key="show"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      initial={{ opacity: 0, rotate: -90 }}
                      animate={{ opacity: 1, rotate: 0 }}
                      exit={{ opacity: 0, rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </motion.svg>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </motion.div>

          {/* Error Message Display */}
          <AnimatePresence>
            {error && (
              <motion.div
                className="text-red-600 text-sm p-3 bg-red-50 rounded-lg border border-red-200 flex items-center"
                initial={{ opacity: 0, scale: 0.8, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <motion.svg
                  className="h-4 w-4 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  initial={{ rotate: -10 }}
                  animate={{ rotate: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </motion.svg>
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.1 }}
          >
            <motion.button
              type="submit"
              className="btn-primary"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <motion.svg
                    className="h-5 w-5 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </motion.svg>
                  Logging in...
                </div>
              ) : (
                "Login"
              )}
            </motion.button>
          </motion.div>

          {/* Forgot Password Link */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            <motion.button
              type="button"
              onClick={onForgotPassword}
              className="text-sm font-medium text-brand-blue hover:text-brand-blue-light transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Forgot Password?
            </motion.button>
          </motion.div>
        </motion.form>

        {/* Demo Credentials */}
        {/* <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            <strong>Demo Credentials:</strong>
            <br />
            Email: admin@tpl.com
            <br />
            Password: admin123
          </p>
        </div> */}
      </motion.div>
    </motion.div>
  );
}
